<template>
  <div class="flex flex-column gap-3">
    <filters-modal :visible.sync="visibleFiltersModal" :current-filters.sync="filters" />
    <create-modal :visible.sync="visibleCreateModal" @add="loadData" />

    <table-layout>
      <template #header><span></span> </template>

      <template #controls-left>
        <a-button class="flex gap-2 align-items-center" type="primary" @click="visibleCreateModal = true">
          <a-icon width="16" height="16" icon="lsicon:work-order-abnormal-filled" />
          Penalize
        </a-button>
      </template>

      <template #controls-right>
        <a-badge :count="countFilters">
          <a-button icon="filter" @click="visibleFiltersModal = true"> Filters </a-button>
        </a-badge>
        <a-button class="flex align-items-center justify-content-center" @click="loadData" title="Refresh Page">
          <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
        </a-button>
      </template>

      <template #filters>
        <active-table-filters
          :items="activeFilterTags"
          @edit="openFilterEditor"
          @remove="removeFilter"
          @reset="resetFilters"
        />
      </template>

      <a-table
        size="small"
        :style="{ whiteSpace: 'pre' }"
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="pagination"
        :rowKey="record => record.id"
        :scroll="{ y: 380, x: 900 }"
        :rowClassName="getRowClassName"
        @change="handleTableChange"
      >
        <span slot="author" slot-scope="text, record">
          <grouped-associate-info
            :first-name="record.author.first_name"
            :last-name="record.author.last_name"
            :photo-url="record.author.photo_url"
            :associate-number="record.author.associate_number"
          />
        </span>
        <span slot="shifts" slot-scope="text, record">
          <div v-for="shift in record.shifts" :key="shift.id">
            <a-button type="link" class="h-fit p-0" @click="openShiftModal(shift.id, record.shifts)">
              {{ formatDateRange(shift.scheduled_start, shift.scheduled_end) }}
            </a-button>
          </div>
        </span>
        <span slot="created_at" slot-scope="text, record">
          {{ formatDateString(record.created_at) }}
        </span>
        <span slot="status" slot-scope="text, record">
          <a-tag :color="SHIFT_PENALTY_STATUS[record.status]?.color">
            {{ SHIFT_PENALTY_STATUS[record.status]?.label }}
          </a-tag>
        </span>
        <span slot="actions" slot-scope="text, record">
          <a-button @click="cancel(record.id)" title="Cancel" class="flex align-items-center gap-2"
            ><a-icon width="20" height="20" icon="ic:round-cancel"
          /></a-button>
        </span>
      </a-table>
    </table-layout>
  </div>
</template>

<script>
import { Badge, Button, notification, Table, Tag } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import tableMixin from '@/mixins/tableMixin'
import ShiftPenaltyFilters from './shift-penalty-filters.vue'
import ShiftPenaltyModal from './shift-penalty-modal.vue'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import { SHIFT_PENALTY_STATUS } from '@/store/modules/profile/constants'
import util from '@/util'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-table': Table,
    'a-button': Button,
    'a-badge': Badge,
    'a-tag': Tag,
    'grouped-associate-info': GroupedAssociateInfo,
    'filters-modal': ShiftPenaltyFilters,
    'create-modal': ShiftPenaltyModal
  },
  mixins: [tableMixin],
  data() {
    return {
      tableData: [],

      SHIFT_PENALTY_STATUS,

      columns: [
        {
          title: 'Author',
          dataIndex: 'author',
          key: 'author',
          width: 300,
          scopedSlots: { customRender: 'author' },
          sorter: (a, b) => {
            const aName = util.combineAssociateName(a.first_name, a.last_name)
            const bName = util.combineAssociateName(b.first_name, b.last_name)

            return aName == null ? -1 : bName == null ? 1 : aName.localeCompare(bName)
          }
        },
        {
          title: 'Reason',
          key: 'reason',
          dataIndex: 'reason',
          width: 200,
          sorter: (a, b) => a.reason - b.reason
        },
        {
          title: 'Shifts',
          key: 'amount',
          dataIndex: 'amount',
          width: 100,
          sorter: (a, b) => a.amount - b.amount
        },
        {
          title: 'Penalized Shifts',
          key: 'removed_amount',
          width: 150,
          sorter: (a, b) => a.removed_amount - b.removed_amount,
          customRender: record => `${record.removed_amount}/${record.amount}`
        },
        {
          title: 'Dates',
          key: 'shifts',
          width: 200,
          scopedSlots: { customRender: 'shifts' }
        },
        {
          title: 'Status',
          key: 'status',
          width: 150,
          scopedSlots: { customRender: 'status' }
        },
        {
          title: 'Created At',
          dataIndex: 'created_at',
          key: 'created_at',
          width: 120,
          scopedSlots: { customRender: 'created_at' },
          sorter: (a, b) => {
            const dateA = a.created_at ? new Date(a.created_at) : null
            const dateB = b.created_at ? new Date(b.created_at) : null

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            return dateA - dateB
          }
        },
        {
          title: 'Actions',
          dataIndex: 'actions',
          key: 'actions',
          width: 100,
          scopedSlots: {
            customRender: 'actions'
          }
        }
      ],

      loading: false,

      visibleFiltersModal: false,
      visibleCreateModal: false,

      filters: { end_date: undefined, start_date: undefined }
    }
  },
  watch: {
    filters: {
      handler(newVal, prevVal) {
        if (JSON.stringify(newVal) != JSON.stringify(prevVal)) {
          this.pagination.current = 1
          this.loadData()
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    })
  },
  methods: {
    ...profileActions(['getShiftPenalty', 'cancelShiftPenalty']),
    ...modalActions(['openModal']),

    openCreate() {
      this.visibleCreateModal = true
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    formatDateString(dateString) {
      return moment(dateString).format('MM/DD/YYYY')
    },

    formatDateRange(start, end) {
      if (start == null) {
        return '-'
      }

      const format = 'MM/DD/YYYY'

      const startDate = util.convertDateTimeToCurrentTimeZoneWithFormat(start, format)
      const endDate = util.convertDateTimeToCurrentTimeZoneWithFormat(end, format)

      if (startDate == endDate || !endDate) {
        return startDate
      }

      return `${startDate} - ${endDate}`
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }

      this.loadData()
    },

    async loadData() {
      this.loading = true

      const payload = {
        associate_id: this.associate.id,
        page: this.pagination.current,
        size: this.pagination.pageSize,
        filter: {
          ...this.filters
        }
      }

      try {
        const data = await this.getShiftPenalty(payload)

        this.tableData = data.items
        this.pagination.total = data.total_count
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loading = false
      }
    },

    async cancel(id) {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Cancel Suspension',
          description: 'Are you sure you want to cancel this Suspension? ',
          action: 'cancel',
          onConfirm: async () => {
            await this.cancelShiftPenalty({ id })

            this.showNotification('success', 'Info', 'Shift penalty has been cancelled successfully')

            this.loadData()
          }
        }
      })
    },

    openShiftModal(shift_id, shifts = []) {
      this.openModal({
        type: 'shiftView',
        data: {
          visible: true,
          edit: () => this.loadData(),
          shift_id,
          shifts_list: shifts.map(item => item.id)
        }
      })
    }
  }
}
</script>

<style scoped>
::v-deep .ant-table-row-cell-break-word {
  text-wrap: initial;
}
</style>
